import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  ShieldCheck,
  MessageSquare,
  Megaphone,
  FileText,
  BookUser,
  ChevronDown,
  Users,
  FileUp,
  CreditCard,
  Inbox,
  Sparkles,
  Zap,
  Code2,
  History,
  X,
  Settings,
  ChevronLeft,
  BarChart2,
  CalendarClock,
  Users2,
  FileBarChart,
  TrendingUp,
  HelpCircle,
} from "lucide-react";
import logo from "../logo.png";
import { WhatsAppAccountSwitcher } from "./ui/WhatsAppAccountSwitcher";
import { isOwnerOrAdmin, hasAnyPermission, getUser } from "../utils/auth";

const mainItems = [
  { path: "/dashboard", label: "Dashboard",         icon: LayoutDashboard, permissions: ["r_campaign", "r_account", "r_message", "r_recipient", "r_template"] },
  { path: "/account",   label: "WhatsApp Accounts", icon: ShieldCheck,     permissions: ["r_account", "crud_account"] },
  { path: "/chat",      label: "Chat",              icon: MessageSquare,   permissions: ["r_message", "s_message"] },
  { path: "/inbox",     label: "Team Inbox",        icon: Inbox,           permissions: ["r_message", "s_message"] },
  { path: "/template",  label: "Templates",         icon: FileText,        permissions: ["r_template", "crud_template", "reg_template", "sync_template"] },
];

const campaignItems = [
  { path: "/campaigns",           label: "Campaigns", icon: Megaphone,     permissions: ["r_campaign", "crud_campaign"] },
  { path: "/campaigns/reports",   label: "Reports",   icon: BarChart2,     permissions: ["r_campaign", "crud_campaign"] },
  { path: "/campaigns/analytics", label: "Analytics", icon: TrendingUp,    permissions: ["r_campaign", "crud_campaign"] },
  { path: "/campaigns/scheduled", label: "Scheduled", icon: CalendarClock, permissions: ["r_campaign", "crud_campaign"] },
];

const contactItems = [
  { path: "/contact",       label: "Contacts",        icon: BookUser,      permissions: ["r_recipient", "crud_recipient"] },
  { path: "/followups",     label: "Follow-Ups Hub",  icon: CalendarClock, permissions: ["r_recipient", "crud_recipient"] },
  { path: "/group",         label: "Groups",          icon: Users,         permissions: ["r_group", "crud_group"] },
  { path: "/importcontact", label: "Import",          icon: FileUp,        permissions: ["r_recipient", "crud_recipient"] },
];

const automationItems = [
  { path: "/auto-replies",  label: "Auto Replies",  icon: Sparkles, permissions: ["r_message", "s_message"] },
  { path: "/quick-replies", label: "Quick Replies", icon: Zap,      permissions: ["r_message", "s_message"] },
];

const settingsItems = [
  { path: "/staff",        label: "Team & Staff",    icon: Users2,      roles: ["owner", "admin"] },
  { path: "/developer",    label: "Developer / API", icon: Code2,       roles: ["owner", "admin"] },
  { path: "/activity-log", label: "Activity Log",    icon: History,     roles: ["owner", "admin"] },
  { path: "/billing",      label: "Billing",         icon: CreditCard,  roles: ["owner", "admin"] },
  { path: "/support",      label: "Help & Support",  icon: HelpCircle },
];

const SectionLabel = ({ label, collapsed, first = false }) => (
  <div
    className={`overflow-hidden whitespace-nowrap transition-all duration-300 ease-in-out
      ${collapsed
        ? "max-h-0 opacity-0 my-0 py-0 -translate-x-2 pointer-events-none duration-150"
        : `max-h-8 opacity-100 translate-x-0 ${first ? "mt-1" : "mt-3.5"} mb-1 delay-100`
      }`}
  >
    <p className="px-3 text-[10px] font-bold uppercase tracking-[0.12em] text-ink-300 dark:text-ink-500">
      {label}
    </p>
  </div>
);

const NavItem = ({ path, label, icon: Icon, active, collapsed, activeRef, onClick }) => (
  <Link
    to={path}
    onClick={onClick}
    ref={active ? activeRef : null}
    title={collapsed ? label : undefined}
    className={`group relative flex items-center gap-3 rounded-xl py-2.5 text-[13px] font-medium
      transition-all duration-300 ease-in-out mb-0.5
      ${active
        ? "bg-brand-600 text-white shadow-sm shadow-brand-200/60 dark:shadow-brand-950/40"
        : "text-ink-500 dark:text-ink-400 hover:bg-ink-50 dark:hover:bg-ink-800/80 hover:text-ink-900 dark:hover:text-white"
      }
      ${collapsed ? "justify-center px-0 mx-1" : "px-2.5"}`}
  >
    {/* Active indicator pill */}
    {active && !collapsed && (
      <span className="absolute left-0 top-1/2 h-4 w-[3px] -translate-y-1/2 rounded-r-full bg-white/60 transition-all duration-200" />
    )}

    {/* Icon Container */}
    <span
      className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition-all duration-300
        ${active
          ? "bg-white/20"
          : "bg-ink-100 dark:bg-ink-800 group-hover:bg-brand-50 dark:group-hover:bg-ink-700"
        }`}
    >
      <Icon
        className={`h-3.5 w-3.5 transition-colors duration-200
          ${active
            ? "text-white"
            : "text-ink-500 dark:text-ink-400 group-hover:text-brand-600 dark:group-hover:text-brand-400"
          }`}
      />
    </span>

    {/* Label with Smooth Slide/Fade with Delay */}
    <span
      className={`flex-1 min-w-0 overflow-hidden whitespace-nowrap transition-all duration-300 ease-in-out
        ${collapsed
          ? "max-w-0 opacity-0 -translate-x-3 pointer-events-none duration-150"
          : "max-w-[190px] opacity-100 translate-x-0 delay-100"
        }`}
    >
      {label}
    </span>

    {/* Tooltip when Collapsed with Hover Delay */}
    {collapsed && (
      <span
        className="pointer-events-none absolute left-full ml-3 whitespace-nowrap rounded-lg
          bg-ink-900 dark:bg-ink-800 border border-transparent dark:border-ink-700 px-2.5 py-1.5 text-[12px] font-medium text-white shadow-lg
          transition-all duration-200 ease-out opacity-0 -translate-x-1.5
          group-hover:opacity-100 group-hover:translate-x-0 group-hover:delay-150 z-50"
      >
        {label}
      </span>
    )}
  </Link>
);

const Sidebar = ({ isOpen, onClose, collapsed, onToggleCollapse }) => {
  const { pathname } = useLocation();
  const [contactsOpen, setContactsOpen] = useState(
    contactItems.some((i) => i.path === pathname)
  );
  const [campaignsOpen, setCampaignsOpen] = useState(
    pathname.startsWith("/campaigns")
  );
  const [settingsOpen, setSettingsOpen] = useState(
    settingsItems.some((i) => i.path === pathname)
  );
  const activeNavRef = useRef(null);

  // Auto-close contacts dropdown when navigating away from contact routes
  useEffect(() => {
    if (!contactItems.some((i) => i.path === pathname)) {
      setContactsOpen(false);
    } else {
      setContactsOpen(true);
    }
  }, [pathname]);

  // Auto-open/close campaigns dropdown based on active route
  useEffect(() => {
    if (!pathname.startsWith("/campaigns")) {
      setCampaignsOpen(false);
    } else {
      setCampaignsOpen(true);
    }
  }, [pathname]);

  // Auto-open/close settings dropdown based on active route
  useEffect(() => {
    if (settingsItems.some((i) => i.path === pathname)) {
      setSettingsOpen(true);
    }
  }, [pathname]);

  useEffect(() => {
    onClose?.();
  }, [pathname]);

  // Scroll active nav item into view smoothly
  useEffect(() => {
    if (activeNavRef.current) {
      activeNavRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [pathname]);

  const user = getUser();

  const filterItem = (item) => {
    if (isOwnerOrAdmin()) return true;
    if (item.roles && !item.roles.includes(user?.role)) return false;
    if (!item.permissions || item.permissions.length === 0) return true;
    return hasAnyPermission(item.permissions);
  };

  const visibleMainItems = mainItems.filter(filterItem);
  const visibleCampaignItems = campaignItems.filter(filterItem);
  const visibleContactItems = contactItems.filter(filterItem);
  const visibleAutomationItems = automationItems.filter(filterItem);
  const visibleSettingsItems = settingsItems.filter(filterItem);

  const isContactActive = visibleContactItems.some((i) => i.path === pathname);
  const isCampaignActive = pathname.startsWith("/campaigns");
  const isSettingsActive = visibleSettingsItems.some((i) => i.path === pathname);

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-30 bg-ink-950/30 dark:bg-ink-950/60 backdrop-blur-[2px] lg:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-40 flex h-screen shrink-0 flex-col
          bg-white dark:bg-ink-900 border-r border-ink-100 dark:border-ink-800
          transition-[width,transform,background-color,border-color] duration-300 ease-in-out
          lg:static lg:translate-x-0
          ${isOpen ? "translate-x-0" : "-translate-x-full"}
          ${collapsed ? "w-[64px]" : "w-64"}`}
      >
        {/* Logo Container */}
        <div
          className={`flex h-14 items-center border-b border-ink-100 dark:border-ink-800 shrink-0 transition-all duration-300
            ${collapsed ? "justify-center px-0" : "justify-between px-4"}`}
        >
          <Link to="/dashboard" onClick={onClose} className="flex items-center gap-2.5 min-w-0 overflow-hidden">
            <img src={logo} alt="WhatsSend" className="h-8 w-8 shrink-0 rounded-xl object-contain transition-transform duration-300" />
            <div
              className={`min-w-0 overflow-hidden whitespace-nowrap transition-all duration-300 ease-in-out
                ${collapsed
                  ? "max-w-0 opacity-0 -translate-x-2 pointer-events-none duration-150"
                  : "max-w-[160px] opacity-100 translate-x-0 delay-100"
                }`}
            >
              <p className="text-[13px] font-bold text-ink-900 dark:text-white leading-none">WhatsSend</p>
              <p className="text-[10px] text-ink-400 dark:text-ink-500 leading-none mt-0.5">Business Messaging</p>
            </div>
          </Link>

          {/* Mobile close button */}
          <button className="text-ink-400 hover:text-ink-600 dark:text-ink-400 dark:hover:text-ink-200 lg:hidden ml-auto mr-1" onClick={onClose}>
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* WhatsApp Multi-Account Switcher */}
        {hasAnyPermission(["r_account", "crud_account"]) && (
          <WhatsAppAccountSwitcher variant="sidebar" collapsed={collapsed} />
        )}

        {/* Navigation Feed */}
        <nav
          className={`flex-1 overflow-y-auto overflow-x-hidden pt-1 pb-2 space-y-0 scroll-smooth
            transition-all duration-300
            ${collapsed ? "px-0" : "px-3"}`}
        >
          {visibleMainItems.length > 0 && (
            <>
              <SectionLabel label="Main" collapsed={collapsed} first={true} />
              {visibleMainItems.map((item) => (
                <NavItem key={item.path} {...item} active={pathname === item.path} collapsed={collapsed} activeRef={activeNavRef} onClick={onClose} />
              ))}
            </>
          )}

          {/* ── Campaigns Accordion ── */}
          {visibleCampaignItems.length > 0 && (
            <>
              <SectionLabel label="Campaigns" collapsed={collapsed} />
              {collapsed ? (
                visibleCampaignItems.map((item) => (
                  <NavItem key={item.path} {...item} active={pathname === item.path} collapsed={collapsed} activeRef={activeNavRef} onClick={onClose} />
                ))
              ) : (
                <>
                  <button
                    onClick={() => setCampaignsOpen((v) => !v)}
                    className={`group flex w-full items-center gap-3 rounded-xl px-2.5 py-2.5 text-[13px]
                      font-medium transition-all duration-200 mb-0.5
                      ${isCampaignActive
                        ? "bg-brand-600 text-white shadow-sm shadow-brand-200/60 dark:shadow-brand-950/40"
                        : "text-ink-500 dark:text-ink-400 hover:bg-ink-50 dark:hover:bg-ink-800/80 hover:text-ink-900 dark:hover:text-white"
                      }`}
                  >
                    <span
                      className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition-colors
                        ${isCampaignActive ? "bg-white/20" : "bg-ink-100 dark:bg-ink-800 group-hover:bg-brand-50 dark:group-hover:bg-ink-700"}`}
                    >
                      <Megaphone className={`h-3.5 w-3.5 ${isCampaignActive ? "text-white" : "text-ink-500 dark:text-ink-400 group-hover:text-brand-600 dark:group-hover:text-brand-400"}`} />
                    </span>
                    <span className="flex-1 text-left">Campaigns</span>
                    <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${campaignsOpen ? "rotate-180" : ""}`} />
                  </button>

                  <div
                    className={`overflow-hidden transition-all duration-300 ease-in-out
                      ${campaignsOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"}`}
                  >
                    <div className="ml-4 flex flex-col border-l-2 border-brand-100 dark:border-brand-900/40 pl-2 pb-1 pt-0.5">
                      {visibleCampaignItems.map((item) => (
                        <NavItem key={item.path} {...item} active={pathname === item.path} collapsed={false} activeRef={activeNavRef} onClick={onClose} />
                      ))}
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {/* ── Contacts Accordion ── */}
          {visibleContactItems.length > 0 && (
            <>
              <SectionLabel label="Contacts" collapsed={collapsed} />
              {collapsed ? (
                visibleContactItems.map((item) => (
                  <NavItem key={item.path} {...item} active={pathname === item.path} collapsed={collapsed} activeRef={activeNavRef} onClick={onClose} />
                ))
              ) : (
                <>
                  <button
                    onClick={() => setContactsOpen((v) => !v)}
                    className={`group flex w-full items-center gap-3 rounded-xl px-2.5 py-2.5 text-[13px]
                      font-medium transition-all duration-200 mb-0.5
                      ${isContactActive
                        ? "bg-brand-600 text-white shadow-sm shadow-brand-200/60 dark:shadow-brand-950/40"
                        : "text-ink-500 dark:text-ink-400 hover:bg-ink-50 dark:hover:bg-ink-800/80 hover:text-ink-900 dark:hover:text-white"
                      }`}
                  >
                    <span
                      className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition-colors
                        ${isContactActive ? "bg-white/20" : "bg-ink-100 dark:bg-ink-800 group-hover:bg-brand-50 dark:group-hover:bg-ink-700"}`}
                    >
                      <BookUser className={`h-3.5 w-3.5 ${isContactActive ? "text-white" : "text-ink-500 dark:text-ink-400 group-hover:text-brand-600 dark:group-hover:text-brand-400"}`} />
                    </span>
                    <span className="flex-1 text-left">Manage Contacts</span>
                    <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${contactsOpen ? "rotate-180" : ""}`} />
                  </button>

                  <div
                    className={`overflow-hidden transition-all duration-300 ease-in-out
                      ${contactsOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"}`}
                  >
                    <div className="ml-4 flex flex-col border-l-2 border-brand-100 dark:border-brand-900/40 pl-2 pb-1 pt-0.5">
                      {visibleContactItems.map((item) => (
                        <NavItem key={item.path} {...item} active={pathname === item.path} collapsed={false} activeRef={activeNavRef} onClick={onClose} />
                      ))}
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {/* ── Automation ── */}
          {visibleAutomationItems.length > 0 && (
            <>
              <SectionLabel label="Automation" collapsed={collapsed} />
              {visibleAutomationItems.map((item) => (
                <NavItem key={item.path} {...item} active={pathname === item.path} collapsed={collapsed} activeRef={activeNavRef} onClick={onClose} />
              ))}
            </>
          )}

          {/* ── Settings Accordion ── */}
          {visibleSettingsItems.length > 0 && (
            <>
              <SectionLabel label="Settings" collapsed={collapsed} />
              {collapsed ? (
                visibleSettingsItems.map((item) => (
                  <NavItem key={item.path} {...item} active={pathname === item.path} collapsed={collapsed} activeRef={activeNavRef} onClick={onClose} />
                ))
              ) : (
                <>
                  <button
                    onClick={() => setSettingsOpen((v) => !v)}
                    className={`group flex w-full items-center gap-3 rounded-xl px-2.5 py-2.5 text-[13px]
                      font-medium transition-all duration-200 mb-0.5
                      ${isSettingsActive
                        ? "bg-brand-600 text-white shadow-sm shadow-brand-200/60 dark:shadow-brand-950/40"
                        : "text-ink-500 dark:text-ink-400 hover:bg-ink-50 dark:hover:bg-ink-800/80 hover:text-ink-900 dark:hover:text-white"
                      }`}
                  >
                    <span
                      className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition-colors
                        ${isSettingsActive ? "bg-white/20" : "bg-ink-100 dark:bg-ink-800 group-hover:bg-brand-50 dark:group-hover:bg-ink-700"}`}
                    >
                      <Settings className={`h-3.5 w-3.5 ${isSettingsActive ? "text-white" : "text-ink-500 dark:text-ink-400 group-hover:text-brand-600 dark:group-hover:text-brand-400"}`} />
                    </span>
                    <span className="flex-1 text-left">Settings</span>
                    <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${settingsOpen ? "rotate-180" : ""}`} />
                  </button>

                  <div
                    className={`overflow-hidden transition-all duration-300 ease-in-out
                      ${settingsOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"}`}
                  >
                    <div className="ml-4 flex flex-col border-l-2 border-brand-100 dark:border-brand-900/40 pl-2 pb-1 pt-0.5">
                      {visibleSettingsItems.map((item) => (
                        <NavItem key={item.path} {...item} active={pathname === item.path} collapsed={false} activeRef={activeNavRef} onClick={onClose} />
                      ))}
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </nav>

        {/* User profile footer with smooth collapse transition */}
        {user && (
          <div className="border-t border-ink-100 dark:border-ink-800 p-2.5 shrink-0 transition-all duration-300">
            <div
              className={`flex items-center gap-2.5 rounded-xl transition-all duration-300 hover:bg-ink-50 dark:hover:bg-ink-800/60
                ${collapsed ? "justify-center p-1.5" : "p-2"}`}
            >
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-100 dark:bg-brand-950/70 text-[12px] font-bold text-brand-700 dark:text-brand-300 shadow-2xs">
                {user.name?.charAt(0).toUpperCase() || "U"}
              </div>
              <div
                className={`min-w-0 flex-1 overflow-hidden whitespace-nowrap transition-all duration-300 ease-in-out
                  ${collapsed
                    ? "max-w-0 opacity-0 -translate-x-2 pointer-events-none duration-150"
                    : "max-w-[150px] opacity-100 translate-x-0 delay-100"
                  }`}
              >
                <p className="truncate text-[12px] font-semibold text-ink-900 dark:text-ink-100">{user.name}</p>
                <p className="truncate text-[11px] text-ink-400 dark:text-ink-500">{user.email}</p>
              </div>
              {!collapsed && (
                <Link
                  to="/profile"
                  onClick={onClose}
                  className="text-ink-400 hover:text-ink-700 dark:text-ink-400 dark:hover:text-ink-200 transition-colors"
                >
                  <Settings className="h-3.5 w-3.5" />
                </Link>
              )}
            </div>
          </div>
        )}

        {/* Desktop Collapse Toggle Button */}
        <button
          onClick={onToggleCollapse}
          className={`hidden lg:flex absolute -right-3 top-[52px] h-6 w-6 items-center justify-center
            rounded-full border border-ink-200 dark:border-ink-700 bg-white dark:bg-ink-800 text-ink-400 dark:text-ink-300 shadow-sm
            hover:bg-ink-50 dark:hover:bg-ink-700 hover:text-ink-700 dark:hover:text-white transition-all duration-200 z-50`}
        >
          <ChevronLeft className={`h-3.5 w-3.5 transition-transform duration-300 ${collapsed ? "rotate-180" : ""}`} />
        </button>
      </aside>
    </>
  );
};

export default Sidebar;
